// Ported from dev-scrips-pm-react src/billing/services/use-provider-fees.ts (PROD-1210 Provider Fees data layer).
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del, patch } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { showSuccess } from '@/shared/lib/toast-helpers';
import type {
  ProviderServiceFee,
  ProviderServiceFeeListResponse,
} from '@/shared/types/billing';

/**
 * Provider Service Fees CRUD hooks.
 *
 * Mirrors Flutter `billing_data_source_impl.dart:343-473`:
 *   GET    /ProviderServiceFees?providerId=&showArchived=&q=&locationId=&pageNumber=&pageSize=
 *   GET    /ProviderServiceFees/{id}
 *   POST   /ProviderServiceFees                         — create
 *   PUT    /ProviderServiceFees/{id}                    — update (Flutter uses PUT, not PATCH)
 *   DELETE /ProviderServiceFees     body=string[]       — batch archive
 *   PATCH  /ProviderServiceFees     body=string[]       — batch unarchive
 *
 * The list-key shape mirrors PA's billing service hooks
 * (`use-payments.ts`): a root array (`['billing', 'provider-fees']`) for
 * cross-mutation invalidation, plus a per-query suffix capturing the
 * filters so different filter combinations don't trample each other in
 * cache.
 */

const KEY_ROOT = ['billing', 'provider-fees'] as const;

export interface UseProviderFeesParams {
  providerId: string;
  showArchived?: boolean;
  q?: string;
  locationId?: string;
  pageNumber?: number;
  pageSize?: number;
}

function listKey(p: UseProviderFeesParams) {
  return [
    ...KEY_ROOT,
    'list',
    p.providerId,
    p.showArchived ?? false,
    p.q ?? '',
    p.locationId ?? '',
    p.pageNumber ?? 1,
    p.pageSize ?? 10,
  ] as const;
}

export function useProviderFees(params: UseProviderFeesParams) {
  const {
    providerId,
    showArchived = false,
    q,
    locationId,
    pageNumber = 1,
    pageSize = 10,
  } = params;

  return useQuery({
    queryKey: listKey(params),
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams();
      qs.set('providerId', providerId);
      qs.set('showArchived', String(showArchived));
      qs.set('pageNumber', String(pageNumber));
      qs.set('pageSize', String(pageSize));
      if (locationId) qs.set('locationId', locationId);
      if (q) qs.set('q', q);
      return get<ProviderServiceFeeListResponse>(
        `${BILLING_BASE}/ProviderServiceFees?${qs.toString()}`,
        { signal },
      );
    },
    enabled: Boolean(providerId),
  });
}

export function useProviderFee(serviceId: string | undefined) {
  return useQuery({
    queryKey: [...KEY_ROOT, 'detail', serviceId] as const,
    queryFn: ({ signal }) =>
      get<ProviderServiceFee>(
        `${BILLING_BASE}/ProviderServiceFees/${serviceId}`,
        { signal },
      ),
    enabled: Boolean(serviceId),
  });
}

export function useCreateProviderFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: ProviderServiceFee) =>
      post<unknown>(`${BILLING_BASE}/ProviderServiceFees`, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
      showSuccess('Provider fee successfully created');
    },
  });
}

export function useUpdateProviderFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: ProviderServiceFee) => {
      if (!request.id) {
        throw new Error('useUpdateProviderFee: id is required');
      }
      return put<ProviderServiceFee>(
        `${BILLING_BASE}/ProviderServiceFees/${request.id}`,
        request,
      );
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
      if (data?.id) {
        qc.setQueryData([...KEY_ROOT, 'detail', data.id], data);
      }
      showSuccess('Provider fee successfully updated');
    },
  });
}

/**
 * Archive (soft-delete) one or more provider fees.
 *
 * The wire shape is unusual: `DELETE /ProviderServiceFees` with the id
 * array as the body. Same pattern as service-list archive. Accepts a
 * single id (convenience) or an array.
 */
export function useArchiveProviderFees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string | string[]) => {
      const idList = Array.isArray(ids) ? ids : [ids];
      return del<unknown>(`${BILLING_BASE}/ProviderServiceFees`, idList);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
      const count = Array.isArray(vars) ? vars.length : 1;
      showSuccess(
        count === 1 ? 'Provider fee archived' : `${count} provider fees archived`,
      );
    },
  });
}

/** Unarchive one or more provider fees — `PATCH` on the collection root with `[id]` body. */
export function useUnarchiveProviderFees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string | string[]) => {
      const idList = Array.isArray(ids) ? ids : [ids];
      return patch<unknown>(`${BILLING_BASE}/ProviderServiceFees`, idList);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
      const count = Array.isArray(vars) ? vars.length : 1;
      showSuccess(
        count === 1
          ? 'Provider fee unarchived'
          : `${count} provider fees unarchived`,
      );
    },
  });
}
